import { motion } from 'motion/react';
import { ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface SetupStep {
  title: string;
  description: string;
  action?: {
    label: string;
    onClick?: () => void;
    href?: string;
    external?: boolean;
  };
}

interface SetupStepsProps {
  steps: SetupStep[];
  className?: string;
}

export function SetupSteps({ steps, className }: SetupStepsProps) {
  return (
    <div className={cn('space-y-4', className)}>
      {steps.map((step, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.2, delay: index * 0.1 }}
          className="flex gap-3"
        >
          <div className="flex-shrink-0 flex items-start pt-0.5">
            <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-xs font-semibold text-primary">{index + 1}</span>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-medium text-foreground">{step.title}</h4>
            <p className="mt-0.5 text-sm text-muted-foreground">{step.description}</p>
            {step.action && (
              <div className="mt-2">
                {step.action.href ? (
                  <Button
                    variant="outline"
                    size="sm"
                    asChild
                    className="gap-1.5 h-7 text-xs"
                  >
                    <a
                      href={step.action.href}
                      target={step.action.external ? '_blank' : undefined}
                      rel={step.action.external ? 'noopener noreferrer' : undefined}
                    >
                      {step.action.label}
                      {step.action.external && <ExternalLink className="h-3 w-3" />}
                    </a>
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={step.action.onClick}
                    className="gap-1.5 h-7 text-xs"
                  >
                    {step.action.label}
                  </Button>
                )}
              </div>
            )}
          </div>
        </motion.div>
      ))}
    </div>
  );
}
